// Public wiki reader: /wiki/:slug (home - the first page by position) and
// /wiki/:slug/:pageSlug (any other page). Mirrors play/screens-play.tsx's
// not-found-vs-error distinction: an unknown or private slug (both answer
// 404, indistinguishably, by design) reads as "this wiki isn't published",
// a real network/server failure gets its own honest message instead of
// being folded into the same bucket.
import { useEffect, useState, type ReactNode } from 'react';
import { timeAgo } from '../app/screens-app';
import { BurgerIcon, CenteredLoading, MarketingFooter, MarketingNav, SearchIcon } from '../components/ui-shared';
import { Link } from '../design/router';
import { ApiHttpError } from '../services/api';
import { MarkdownView } from './MarkdownView';
import {
  getPublicWiki,
  getPublicWikiPage,
  listPublicWikis,
  searchWiki,
  type PublicWiki,
  type PublicWikiListing,
  type PublicWikiPage,
  type WikiSearchResult,
} from './wikiPublicClient';

/** Debounced full-text search over a single wiki's own pages. Returns the
 * search box element plus whether a query is currently active, so the
 * caller (WikiLayout) can swap the normal page list out for results while
 * one is - two things that live together logically but render in different
 * places in the sidebar. `onNavigate` fires when a result link is clicked,
 * same as every page link in the sidebar - so picking a search result also
 * closes the mobile sidebar overlay instead of leaving it covering the page
 * it just navigated to. */
function useWikiSearch(wikiSlug: string, onNavigate?: () => void) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WikiSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const handle = setTimeout(() => {
      searchWiki(wikiSlug, q)
        .then((r) => {
          if (!cancelled) setResults(r);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [wikiSlug, query]);

  const active = query.trim().length > 0;

  const box = (
    <div style={{ marginBottom: 10 }}>
      <input
        className="ss-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search this wiki…"
        aria-label="Search this wiki"
        style={{ fontSize: 12.5, padding: '7px 10px' }}
      />
      {active && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
          {searching && <span className="hint">Searching…</span>}
          {!searching && results?.length === 0 && <span className="hint">No matches.</span>}
          {!searching &&
            results?.map((r) => (
              <Link
                key={r.id}
                to={`/wiki/${wikiSlug}/${r.slug}`}
                style={{ display: 'block', padding: '7px 8px', borderRadius: 6 }}
                onClick={onNavigate}
              >
                <span style={{ display: 'block', fontWeight: 600, fontSize: 12.5, color: 'var(--ss-text)' }}>{r.title}</span>
                <span style={{ display: 'block', color: 'var(--ss-text-3)', fontSize: 11.5, lineHeight: 1.4 }}>{r.snippet}</span>
              </Link>
            ))}
        </div>
      )}
    </div>
  );

  return { box, active };
}

type LoadError = 'not-found' | 'error';

function WikiProblem({ kind, detail }: { kind: LoadError; detail?: string }) {
  return (
    <div className="ss-screen">
      <MarketingNav active="Wiki" />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <p style={{ fontSize: 15, color: 'var(--ss-text-2)', marginBottom: 12 }}>
            {kind === 'not-found' ? "This wiki isn't available" : 'Failed to load this wiki'}
          </p>
          <p style={{ fontSize: 13, color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
            {kind === 'not-found'
              ? "The link may be old, or the wiki was made unlisted or private by its creator."
              : (detail ?? 'Could not reach the server. Check your connection and refresh.')}
          </p>
          <Link to="/" className="ss-btn ss-btn--ghost ss-btn--sm" style={{ marginTop: 16, display: 'inline-flex' }}>
            Back to Soulstack
          </Link>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}

// A subtle recency signal only - not a duplicate title. The page's own
// markdown almost always opens with its own # heading (the sidebar link is
// the title otherwise), so this deliberately doesn't repeat it.
function PageMeta({ page }: { page: PublicWikiPage }) {
  if (!page.updated_at) return null;
  return (
    <div style={{ textAlign: 'right', marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>Updated {timeAgo(new Date(page.updated_at).getTime())}</span>
    </div>
  );
}

function WikiLoading() {
  return (
    <div className="ss-screen">
      <MarketingNav active="Wiki" />
      <CenteredLoading />
    </div>
  );
}

function WikiLayout({ wiki, activeSlug, children }: { wiki: PublicWiki; activeSlug: string | null; children: ReactNode }) {
  // Below wiki-reader-nav-burger's breakpoint the page-list sidebar becomes a
  // toggled overlay instead (wiki-reader-sidebar in soulstack.css, same
  // pattern as DocsShell's docs-sidebar) - closed by default, and closed
  // again on every link click below since routing is client-side and
  // otherwise the overlay would keep covering the page it just navigated to.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const { box: searchBox, active: searching } = useWikiSearch(wiki.slug, closeSidebar);
  return (
    <div className="ss-screen" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <MarketingNav active="Wiki" />
      <div className="wiki-reader-topbar">
        <span className="wiki-reader-topbar-title">{wiki.title}</span>
        <button
          type="button"
          className="wiki-reader-nav-burger"
          aria-label={sidebarOpen ? 'Close wiki navigation' : 'Open wiki navigation'}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen((v) => !v)}
        >
          <BurgerIcon open={sidebarOpen} size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        <aside
          className={'wiki-reader-sidebar' + (sidebarOpen ? ' is-open' : '')}
          style={{
            width: 240,
            flexShrink: 0,
            borderRight: '1px solid var(--ss-border-soft)',
            background: 'var(--ss-bg-2)',
            padding: '28px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            overflowY: 'auto',
          }}
        >
          <Link
            to={`/wiki/${wiki.slug}`}
            style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--ss-text)', marginBottom: 14 }}
            onClick={closeSidebar}
          >
            {wiki.title}
          </Link>
          {wiki.pages.length > 1 && searchBox}
          {wiki.pages.length === 0 && <span className="hint">No pages yet.</span>}
          {!searching && wiki.pages.map((p, i) => {
            const isActive = activeSlug === p.slug || (i === 0 && activeSlug === null);
            const to = i === 0 ? `/wiki/${wiki.slug}` : `/wiki/${wiki.slug}/${p.slug}`;
            // Pages stay in one flat, creator-ordered list; section is only a
            // rendering label, shown as a header whenever it changes from the
            // page before it - not a resort, and not a tree (see wikiPublicClient.ts).
            const previousSection = i > 0 ? wiki.pages[i - 1]!.section : undefined;
            const showHeader = p.section && p.section !== previousSection;
            return (
              <div key={p.id}>
                {showHeader && (
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--ss-text-3)',
                      padding: '10px 10px 4px',
                    }}
                  >
                    {p.section}
                  </div>
                )}
                <Link
                  to={to}
                  style={{
                    display: 'block',
                    fontSize: 13.5,
                    padding: '7px 10px',
                    borderRadius: 8,
                    color: isActive ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                    background: isActive ? 'var(--ss-accent-soft)' : 'transparent',
                    fontWeight: isActive ? 600 : 500,
                  }}
                  onClick={closeSidebar}
                >
                  {p.title}
                </Link>
              </div>
            );
          })}
          {wiki.game && (
            <a
              href={wiki.game.url}
              style={{
                marginTop: 'auto',
                paddingTop: 14,
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--ss-text-3)',
                borderTop: '1px solid var(--ss-border-soft)',
              }}
            >
              ← Play {wiki.game.name}
            </a>
          )}
        </aside>
        <main style={{ flex: 1, minWidth: 0, padding: '40px 48px', maxWidth: 760, overflowY: 'auto' }}>{children}</main>
      </div>
    </div>
  );
}

function useWikiHome(slug: string) {
  const [wiki, setWiki] = useState<PublicWiki | null>(null);
  const [page, setPage] = useState<PublicWikiPage | null>(null);
  const [settled, setSettled] = useState(false);
  const [loadError, setLoadError] = useState<LoadError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setWiki(null);
    setPage(null);
    setSettled(false);
    setLoadError(null);
    getPublicWiki(slug)
      .then(async (w) => {
        if (cancelled) return;
        setWiki(w);
        if (w.pages.length > 0) {
          const home = await getPublicWikiPage(slug, w.pages[0]!.slug).catch(() => null);
          if (!cancelled) setPage(home);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiHttpError && err.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { wiki, page, settled, loadError };
}

export function ScreenWikiHome({ slug }: { slug: string }) {
  const { wiki, page, settled, loadError } = useWikiHome(slug);

  if (!settled) return <WikiLoading />;
  if (!wiki) return <WikiProblem kind={loadError ?? 'error'} />;

  return (
    <WikiLayout wiki={wiki} activeSlug={null}>
      {wiki.pages.length === 0 ? (
        <p style={{ color: 'var(--ss-text-3)', fontSize: 13.5 }}>This wiki doesn't have any pages yet.</p>
      ) : page ? (
        <>
          <PageMeta page={page} />
          <MarkdownView source={page.body_markdown} />
        </>
      ) : (
        <WikiLoading />
      )}
    </WikiLayout>
  );
}

export function ScreenWikiPage({ slug, pageSlug }: { slug: string; pageSlug: string }) {
  const [wiki, setWiki] = useState<PublicWiki | null>(null);
  const [page, setPage] = useState<PublicWikiPage | null>(null);
  const [settled, setSettled] = useState(false);
  const [loadError, setLoadError] = useState<LoadError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setWiki(null);
    setPage(null);
    setSettled(false);
    setLoadError(null);
    Promise.all([getPublicWiki(slug), getPublicWikiPage(slug, pageSlug)])
      .then(([w, p]) => {
        if (cancelled) return;
        setWiki(w);
        setPage(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiHttpError && err.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, pageSlug]);

  if (!settled) return <WikiLoading />;
  if (!wiki || !page) return <WikiProblem kind={loadError ?? 'error'} />;

  return (
    <WikiLayout wiki={wiki} activeSlug={pageSlug}>
      <PageMeta page={page} />
      <MarkdownView source={page.body_markdown} />
    </WikiLayout>
  );
}

/* ── Wikis directory (/wikis) ─────────────────────────────────────────────
 * The nav-level "Wiki" destination: every wiki is otherwise reachable only
 * by its own direct /wiki/<slug> link (there is no per-project browsing
 * surface for them), so this is the one page that lists all of them, same
 * discovery-vs-read split as the showcase's relationship to /play. */

// Card anatomy follows ShowcaseCard's own precedent (showcase/screens-showcase.tsx):
// the title is the only element spanning the card, with any crosslink (here,
// the game the wiki belongs to) as its own separate link alongside it -
// nesting another interactive link inside a card-wide Link would produce
// invalid, ambiguously-clickable nested anchors. The game crosslink is a
// plain absolute anchor (wiki.game.url), matching WikiLayout's own
// "← Play {game}" sidebar link just above.
function WikiListingCard({ w }: { w: PublicWikiListing }) {
  return (
    <div className="ss-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--ss-text)' }}>
      <Link to={`/wiki/${w.slug}`} style={{ fontSize: 15.5, fontWeight: 600 }}>{w.title}</Link>
      <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>
        {w.page_count} {w.page_count === 1 ? 'page' : 'pages'}
        {w.updated_at && <> · updated {timeAgo(new Date(w.updated_at).getTime())}</>}
      </span>
      {(w.dev || w.game) && (
        <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>
          {w.dev && <>by <span style={{ color: 'var(--ss-accent)', fontWeight: 500 }}>{w.dev.studio_name}</span></>}
          {w.dev && w.game && ' · '}
          {w.game && <>for <a href={w.game.url} style={{ color: 'var(--ss-text-2)' }}>{w.game.name}</a></>}
        </span>
      )}
    </div>
  );
}

function WikiListingCardSkeleton() {
  return (
    <div className="ss-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
      <div className="ss-skeleton" style={{ width: '60%', height: 15.5 }} />
      <div className="ss-skeleton" style={{ width: '35%', height: 12.5 }} />
    </div>
  );
}

export function ScreenWikisDirectory() {
  const [wikis, setWikis] = useState<PublicWikiListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    listPublicWikis()
      .then((rows) => {
        if (!cancelled) setWikis(rows);
      })
      .catch((err: unknown) => {
        console.error('[ScreenWikisDirectory] failed to load public wikis:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? wikis.filter(
        (w) =>
          w.title.toLowerCase().includes(q) ||
          (w.dev?.studio_name ?? '').toLowerCase().includes(q) ||
          (w.game?.name ?? '').toLowerCase().includes(q),
      )
    : wikis;

  return (
    <div className="ss-screen">
      <MarketingNav active="Wiki" />
      <header style={{ padding: '72px 28px 36px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span className="ss-eyebrow">Wikis</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.25rem)' }}>Creator wikis</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 16.5, maxWidth: 540 }}>
            Lore, guides and reference docs, written by the creators who made each game.
          </p>
        </div>
      </header>
      <section style={{ padding: '0 28px 110px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
          {!loading && wikis.length > 0 && (
            <div className="ss-search" style={{ width: 280, alignSelf: 'flex-end' }}>
              <SearchIcon />
              <input
                className="ss-input"
                placeholder="Search wikis"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '9px 14px 9px 35px', fontSize: 13.5 }}
              />
            </div>
          )}
          {loading ? (
            <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
              <WikiListingCardSkeleton />
              <WikiListingCardSkeleton />
              <WikiListingCardSkeleton />
            </div>
          ) : wikis.length === 0 ? (
            <div style={{ padding: '44px 0 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5 }}>Public wikis will appear here as creators publish them.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--ss-text-3)', fontSize: 14 }}>No wikis match "{searchQuery}".</span>
              <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => setSearchQuery('')}>Clear search</button>
            </div>
          ) : (
            <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
              {filtered.map((w) => <WikiListingCard key={w.slug} w={w} />)}
            </div>
          )}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
